import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Icons copied from boards 18 and 20 (24x24 viewBox, currentColor stroke).
 * All are decorative (aria-hidden); the QuickButton caption names the control.
 */
function StrokeIcon({ size = 18, strokeWidth = 2, className, children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
    >
      {children}
    </svg>
  );
}

/** Funnel: the Filter quick button. */
export function FilterIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 5h18l-7 8v6l-4-2v-4z" />
    </StrokeIcon>
  );
}

/** Circular arrow: the Reset quick button. */
export function ResetIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </StrokeIcon>
  );
}

/**
 * Text marker for the Labels quick button. The board shows "Aa" for note names and
 * "1·3" for intervals, so the text is a prop. Same 18px box as the SVG icons.
 */
export function LabelsIcon({ text = 'Aa', className }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block select-none text-[15px] font-extrabold leading-[18px]', className)}
    >
      {text}
    </span>
  );
}

/** Chevron used by KeyTitle (‹ ›). */
export function ChevronIcon({ direction = 'left', size = 18, className }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.6} className={className}>
      <path d={direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </StrokeIcon>
  );
}

/** Crescent moon: dark-mode button (board 18 / 20 path). */
export function MoonIcon({ size = 16, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.2} {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
    </StrokeIcon>
  );
}

/** Sun: light-mode button, shown while dark mode is on. */
export function SunIcon({ size = 16, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.2} {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </StrokeIcon>
  );
}

/** Down chevron: the instrument chip on the phone header. */
export function ChevronDownIcon({ size = 12, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={3} {...props}>
      <path d="M6 9l6 6 6-6" />
    </StrokeIcon>
  );
}

/** Check mark: the active row in the instrument menu. */
export function CheckIcon({ size = 16, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.6} {...props}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </StrokeIcon>
  );
}

/**
 * The TabBase mark, copied from TabBase's LogoIcon (src/components/ui/Logo.tsx there): a tab
 * staff opening with "TAB", frets 3 and 5. Fixed brand colours like ScaleBase's own logo, so it
 * looks the same in light and dark. Sized by height; the width follows (about 1.36 × height).
 */
export function TabBaseMark({ height = 18, className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="100 138 322 236"
      height={height}
      width={Math.round((height * 322) / 236)}
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0 select-none', className)}
    >
      <g stroke="#A94A36" strokeWidth="9" strokeLinecap="round" fill="none">
        <path d="M108 146H416M212.5 190H416M212.5 234H416M212.5 278H416M212.5 322H416M108 366H416" />
        <path d="M108 146V366" strokeWidth="12" />
      </g>
      <g fill="#A94A36">
        <path d="M156.3 217V174.3H139.1V164.8H185V174.3H167.8V217ZM133.4 283 157.4 230.8H166.6L190.6 283H179.1L172.8 268.5L177.4 271.7H146.6L151.2 268.5L145 283ZM161.9 243 152.4 265.6 150.4 262.7H173.6L171.6 265.6L162 243ZM141.5 349V296.8H165.4Q173.8 296.8 178.4 300.5Q182.9 304.1 182.9 310.4Q182.9 315 180.2 318.3Q177.6 321.5 172.9 322.7V321.5Q176.5 322.1 179.1 323.8Q181.6 325.5 182.9 328.1Q184.3 330.8 184.3 334.3Q184.3 338.9 182.1 342.2Q179.9 345.5 175.9 347.3Q171.9 349 166.3 349ZM152.5 340.5H164.7Q166.5 340.5 167.9 340.2Q169.3 339.9 170.3 339.4Q171.3 338.9 172 338Q172.6 337.2 173 336.1Q173.3 334.9 173.3 333.6Q173.3 332.2 173 331.1Q172.6 330.1 172 329.2Q171.3 328.4 170.3 327.9Q169.3 327.3 167.9 327.1Q166.5 326.8 164.7 326.8H152.5ZM152.5 318.3H163.4Q167.7 318.3 169.8 316.7Q171.9 315 171.9 311.8Q171.9 308.6 169.8 307Q167.7 305.3 163.4 305.3H152.5Z" />
        <circle cx="294" cy="190" r="33" />
        <circle cx="370" cy="322" r="33" />
      </g>
      <g fill="#F8F5EC">
        <path d="M293.2 206.5Q291 206.5 288.8 206.1Q286.6 205.6 284.8 204.8Q283 204 281.7 202.9L283.9 197.5Q286 199.1 288.3 199.8Q290.5 200.6 292.9 200.6Q294.8 200.6 296 200.1Q297.3 199.6 297.9 198.7Q298.6 197.8 298.6 196.4Q298.6 194.5 297.2 193.5Q295.9 192.5 293.3 192.5H287.9V186.8H292.6Q294.2 186.8 295.3 186.3Q296.3 185.8 297 184.9Q297.6 184.1 297.6 182.8Q297.6 180.9 296.3 180Q295.1 179 292.7 179Q290.4 179 288.3 179.8Q286.3 180.5 284.5 182.1L282.2 176.8Q284.2 175 287.2 174.1Q290.1 173.1 293.3 173.1Q296.8 173.1 299.3 174.1Q301.8 175.2 303.1 177.1Q304.4 179.1 304.4 181.8Q304.4 184.7 302.9 186.7Q301.3 188.8 298.5 189.5V188.9Q300.7 189.3 302.2 190.4Q303.8 191.5 304.6 193.2Q305.4 194.9 305.4 197Q305.4 199.9 303.9 202.1Q302.5 204.2 299.7 205.4Q297 206.5 293.2 206.5Z" />
        <path d="M370 338.5Q367.9 338.5 365.8 338Q363.7 337.6 361.9 336.8Q360.1 336 358.8 334.9L361.1 329.5Q363.1 331 365.4 331.8Q367.6 332.6 369.9 332.6Q372.5 332.6 374 331.3Q375.4 330 375.4 327.8Q375.4 325.6 374 324.3Q372.6 323 370.2 323Q368.5 323 367 323.6Q365.6 324.2 364.3 325.6H359.7V305.6H380.3V311.4H366.6V320.2H364.8Q366 318.8 367.9 318Q369.8 317.2 372 317.2Q375.1 317.2 377.4 318.5Q379.7 319.8 381 322.2Q382.3 324.5 382.3 327.6Q382.3 330.8 380.8 333.3Q379.3 335.7 376.6 337.1Q373.8 338.5 370 338.5Z" />
      </g>
    </svg>
  );
}

/** Arrow out of the page: a link that opens another site in a new tab. */
export function ExternalIcon({ size = 14, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.4} {...props}>
      <path d="M7 17L17 7M9 7h8v8" />
    </StrokeIcon>
  );
}

/** X: close button. */
export function CloseIcon({ size = 16, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.4} {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </StrokeIcon>
  );
}

/** Head and shoulders: Sign in / Sign out in the header info menu. */
export function UserIcon({ size = 18, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.2} {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c1.2-3.8 4-5.5 7.5-5.5s6.3 1.7 7.5 5.5" />
    </StrokeIcon>
  );
}

/** Circled "i": the header info menu (FAQ, TabBase). */
export function InfoIcon({ size = 18, ...props }) {
  return (
    <StrokeIcon size={size} strokeWidth={2.2} {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v5.5" />
      <path d="M12 7.6v.01" strokeWidth={3} />
    </StrokeIcon>
  );
}
